// GlobalContext.js - Global context provider for app-wide state management.
// Manages invoice history, last invoice number, current invoice, theme, language, and isLoadedInvoice flag.
// Updated: Added isLoadedInvoice to distinguish new vs. loaded invoices for proper numbering.
// Consolidated history saving into saveInvoiceToHistory for unified logic (increments only for new invoices, updates/replaces history entries).
// Changed default lastInvoiceNumber to '0' to ensure new users start invoice numbering at 1.
// Fixed: Removed unused 'nextNumber' variable in createNewInvoice to eliminate ESLint warning.
// Updated: Sort history by invoice number descending for reverse listing (e.g., #7, #6, #5).
// Fixed: After saving an invoice, update currentInvoice to the newInvoice to keep it in sync with history and prevent form reversion after export (e.g., for loaded invoices, ensures updated data persists in currentInvoice).

import React, { createContext, useState, useEffect } from 'react';

// Create the context
export const GlobalContext = createContext();

// Provider component
export const GlobalProvider = ({ children }) => {
  // State for invoice history (array of invoice objects)
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  // State for the last used invoice number - Default to '0' for new users to start numbering at 1
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState('0');
  // State for the currently loaded invoice (null for new invoices)
  const [currentInvoice, setCurrentInvoice] = useState(null);
  // State for theme ('light' or 'dark'), default to 'light'
  const [theme, setTheme] = useState('light');
  // State for language ('en' or 'es'), default to 'en'
  const [language, setLanguage] = useState('en');
  // New: State for isLoadedInvoice (true if an existing invoice is loaded, false for new invoices)
  const [isLoadedInvoice, setIsLoadedInvoice] = useState(false);

  // Load data from localStorage on mount
  useEffect(() => {
    const storedHistory = localStorage.getItem('invoiceHistory');
    const storedLastNumber = localStorage.getItem('lastInvoiceNumber');
    const storedTheme = localStorage.getItem('theme');
    const storedLanguage = localStorage.getItem('language');
    const storedIsLoaded = localStorage.getItem('isLoadedInvoice');
    if (storedHistory) {
      setInvoiceHistory(JSON.parse(storedHistory));
    }
    if (storedLastNumber) {
      setLastInvoiceNumber(storedLastNumber);
    }
    if (storedTheme) {
      setTheme(storedTheme);
    }
    if (storedLanguage) {
      setLanguage(storedLanguage);
    }
    if (storedIsLoaded) {
      setIsLoadedInvoice(storedIsLoaded === 'true');
    }
  }, []);

  // Save data to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('invoiceHistory', JSON.stringify(invoiceHistory));
  }, [invoiceHistory]);

  useEffect(() => {
    localStorage.setItem('lastInvoiceNumber', lastInvoiceNumber);
  }, [lastInvoiceNumber]);

  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // New: Save isLoadedInvoice to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('isLoadedInvoice', isLoadedInvoice.toString());
  }, [isLoadedInvoice]);

  // New: Function to create a new invoice (resets form, sets next number implicitly via hook, marks as not loaded)
  // Fixed: Removed unused 'nextNumber' variable
  const createNewInvoice = () => {
    setCurrentInvoice(null); // Clear current invoice
    setIsLoadedInvoice(false); // Mark as new
  };

  // Updated: Function to load an invoice into the form (sets currentInvoice and marks as loaded)
  const loadInvoice = (id) => {
    const invoice = invoiceHistory.find((inv) => inv.id === id);
    if (invoice) {
      setCurrentInvoice(invoice);
      setIsLoadedInvoice(true); // Mark as loaded
    }
    return invoice;
  };

  // New: Unified function to save an invoice to history (handles new and updates)
  // Increments lastInvoiceNumber only if not loaded; updates/replaces history entry by invoiceNumber
  // Updated: Sort history by invoice number descending for reverse listing (e.g., #7, #6, #5)
  // Fixed: After updating history, set currentInvoice to newInvoice to sync with saved data and prevent reversion (e.g., for loaded invoices, ensures form retains updates after export)
  const saveInvoiceToHistory = (invoiceData) => {
    //const newInvoice = { ...invoiceData, id: Date.now(), exportedAt: new Date().toISOString() };
    
    // Validate logo as a base64 string to ensure persistence
    const validatedLogo = typeof invoiceData.logo === 'string' && invoiceData.logo.startsWith('data:image/') ? invoiceData.logo : '';
    const newInvoice = { ...invoiceData, id: Date.now(), exportedAt: new Date().toISOString(), logo: validatedLogo };
  
    if (!isLoadedInvoice) {
      // New invoice: Increment global number
      const newLast = (parseInt(lastInvoiceNumber) + 1).toString();
      setLastInvoiceNumber(newLast);
    }
    
    // Update history: Replace if exists (by invoiceNumber), else add; sort by invoice number descending
    setInvoiceHistory((prev) => {
      const filtered = prev.filter(h => h.invoiceNumber !== newInvoice.invoiceNumber);
      const updated = [...filtered, newInvoice];
      return updated.sort((a, b) => parseInt(b.invoiceNumber) - parseInt(a.invoiceNumber));
    });

    // Fixed: Sync currentInvoice with the saved invoice to prevent form reversion after export
    setCurrentInvoice(newInvoice);
    if (!isLoadedInvoice) {
      setIsLoadedInvoice(true); // Mark as loaded for new invoices
    }
  };

  // Function to delete an invoice from history
  const deleteInvoiceFromHistory = (id) => {
    setInvoiceHistory((prev) => prev.filter((inv) => inv.id !== id));
  };

  // Function to clear currentInvoice (for new invoices) - now calls createNewInvoice for consistency
  const clearCurrentInvoice = () => {
    createNewInvoice();
  };

  // Function to toggle theme between 'light' and 'dark'
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Function to set language ('en' or 'es')
  const changeLanguage = (lng) => {
    setLanguage(lng);
  };

  return (
    <GlobalContext.Provider
      value={{
        invoiceHistory,
        lastInvoiceNumber,
        currentInvoice,
        theme,
        language,
        isLoadedInvoice, // New: Expose isLoadedInvoice
        createNewInvoice, // New: Expose createNewInvoice
        saveInvoiceToHistory, // New: Unified save function
        deleteInvoiceFromHistory,
        loadInvoice,
        clearCurrentInvoice,
        toggleTheme,
        changeLanguage,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};